<template>
  <!-- « Solde et forfait »: the plan and its state first (what was missing from the old page of offers), the money next,
       and at the very bottom, quiet, the way out. A plan that ends offers to keep it instead. -->
  <div class="plan-menu">
    <section class="plan-menu__section" aria-labelledby="plan-menu-plan">
      <p id="plan-menu-plan" class="plan-menu__label">{{ t("billing.menu.plan") }}</p>
      <div class="plan-menu__plan">
        <span class="plan-menu__name">{{ planName }}</span>
        <span v-if="price" class="plan-menu__price">{{ t("billing.menu.perMonth", { price }) }}</span>
      </div>
      <p class="plan-menu__state" :class="`plan-menu__state--${ state.key }`" aria-live="polite">
        <span v-if="!summary && loading" class="plan-menu__skeleton" aria-hidden="true" />
        <template v-else>{{ stateLine }}</template>
      </p>
      <div class="plan-menu__actions">
        <UButton v-if="ending" color="neutral" size="xs" icon="i-heroicons-arrow-path" :loading="resuming" @click="keep">
          {{ t("billing.menu.resume") }}
        </UButton>
        <NuxtLink class="plan-menu__link" to="/subscriptions" @click="$emit('navigate')">{{ t("billing.menu.change") }}</NuxtLink>
      </div>
    </section>

    <section class="plan-menu__section" aria-labelledby="plan-menu-balance">
      <div class="plan-menu__row">
        <p id="plan-menu-balance" class="plan-menu__label">{{ t("billing.menu.balance") }}</p>
        <NuxtLink class="plan-menu__link" to="/settings/billing#credit" @click="$emit('navigate')">{{ t("billing.menu.credit") }}</NuxtLink>
      </div>
      <p class="plan-menu__amount">{{ text2Price($account?.balance ?? 0) }}</p>
      <p class="plan-menu__muted">
        {{ t("billing.menu.minimal", { amount: text2Price($account?.minimal_balance ?? 0) }) }} ·
        <NuxtLink class="plan-menu__link plan-menu__link--quiet" to="/settings/billing#credit" @click="$emit('navigate')">
          {{ t("billing.menu.minimalEdit") }}
        </NuxtLink>
      </p>
    </section>

    <section class="plan-menu__section">
      <NuxtLink class="plan-menu__link" to="/settings/billing#invoices_history" @click="$emit('navigate')">{{ t("billing.menu.invoices") }}</NuxtLink>
    </section>

    <!-- Present but not pushed: a person who wants to leave finds it, nobody stumbles on it. -->
    <footer v-if="!ending" class="plan-menu__footer">
      <button type="button" class="plan-menu__leave" @click="leave">{{ t("billing.menu.cancel") }}</button>
    </footer>
  </div>
</template>

<script setup>
import { text2Price } from "~/utils/global.mjs";
import { longDate, planState } from "~/utils/billing";
import { plainName } from "~/utils/welcome";

const emit = defineEmits(["navigate", "cancel"]);

const { t, locale } = useI18n();
const { $account } = useNuxtApp();
const { summary, loading, failed, load, keepPlan, openCancel } = useSubscription();
const { trackCancellation } = useTracking();

// Read again at each opening: a cancellation made from another device, or a renewal, shows at once.
onMounted(load);

const plan = computed(() => summary.value?.plan ?? $account?.subscription_plan ?? null);
const planName = computed(() => {
  const key = plan.value?.displayname;
  if (!key) return t("billing.menu.state.none");
  const label = t(`subscriptions.${ key }`);
  return plainName(label === `subscriptions.${ key }` ? key : label);
});
const price = computed(() => (Number(plan.value?.price) > 0 ? text2Price(plan.value.price, 0) : ""));

const state = computed(() => (summary.value ? planState(summary.value) : { key: failed.value ? "unknown" : "none", date: null }));
const stateLine = computed(() => {
  const { key, date } = state.value;
  if (!summary.value && !failed.value) return "";
  return t(`billing.menu.state.${ key }`, { date: longDate(date, locale.value) });
});
const ending = computed(() => !!summary.value?.endsAt);

const resuming = ref(false);
const keep = async () => {
  resuming.value = true;
  await keepPlan();
  resuming.value = false;
};

const leave = () => {
  trackCancellation({ step: "opened" });
  emit("cancel");
  openCancel();
};
</script>

<style lang="scss" scoped>
// Same grammar as the getting started (assets/scss/pages/onboarding.scss): headings #344767, text #67748e, links #4553c9.
.plan-menu {
  display: flex;
  flex-direction: column;
  width: min(320px, calc(100vw - 32px));
  padding: 6px 0;
  color: #344767;

  &__section {
    padding: 14px 18px;

    & + & {
      border-top: 1px solid #e4e7f0;
    }
  }

  &__label {
    color: #67748e;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }

  &__plan {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    margin-top: 6px;
  }

  &__name {
    font-size: 15px;
    font-weight: 600;
  }

  &__price {
    flex: none;
    color: #67748e;
    font-size: 13px;
  }

  &__state {
    min-height: 20px;
    margin-top: 4px;
    color: #67748e;
    font-size: 13px;

    &--trial,
    &--renews,
    &--manual {
      color: #1b6b47;
    }

    &--ends,
    &--trialEnds,
    &--pastDue {
      color: #8a4b00;
      font-weight: 500;
    }
  }

  &__skeleton {
    display: inline-block;
    width: 70%;
    height: 12px;
    border-radius: 6px;
    background: #eef1fa;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 14px;
    margin-top: 10px;
  }

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  &__amount {
    margin-top: 4px;
    font-size: 18px;
    font-weight: 600;
  }

  &__muted {
    margin-top: 2px;
    color: #67748e;
    font-size: 12.5px;
  }

  &__link {
    color: #4553c9;
    font-size: 13.5px;
    font-weight: 500;
    text-decoration: underline;
    text-underline-offset: 2px;

    &:hover {
      color: #344767;
    }

    &--quiet {
      font-size: inherit;
      font-weight: 400;
    }

    &:focus-visible {
      outline: 2px solid #4553c9;
      outline-offset: 2px;
      border-radius: 2px;
    }
  }

  &__footer {
    padding: 10px 18px 8px;
    border-top: 1px solid #e4e7f0;
  }

  &__leave {
    color: #67748e;
    font-size: 12.5px;
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;

    &:hover {
      color: #344767;
    }

    &:focus-visible {
      outline: 2px solid #4553c9;
      outline-offset: 2px;
      border-radius: 2px;
    }
  }
}
</style>
